import type { FC, ReactNode } from "react";
import { Flex, Spacing, Text } from "@huub-web/ui";

import {
  contentColumnStyle,
  descriptionStyle,
  imagePanelStyle,
  imageStyle,
  titleStyle,
  wrapperStyle,
} from "./FundaBanner.css";

type FundaBannerProps = {
  children?: ReactNode;
  /**
   * Banner title displayed in the left panel
   */
  title: string;
  /**
   * Banner description displayed below the title
   */
  description: string;
  /**
   * URL of the image to display in the right panel
   */
  imageSrc: string;
  /**
   * CSS object-position for the image (defaults to "center bottom")
   */
  imagePosition?: string;
  /**
   * CSS width/height override for the image (e.g. "150%" for zoomed-in SVGs)
   */
  imageScale?: string;
};

/**
 * Reusable two-column banner for Funda theme slot overrides.
 */
export const FundaBanner: FC<FundaBannerProps> = ({
  children,
  title,
  description,
  imageSrc,
  imagePosition,
  imageScale,
}) => {
  return (
    <>
      <Spacing height="m" />
      <Flex direction="column" className={wrapperStyle}>
        <Flex direction="column" gap="m" className={contentColumnStyle}>
          <Text variant="displayM" bold className={titleStyle}>
            {title}
          </Text>
          <Text
            variant="bodyM"
            color="currentColor75"
            className={descriptionStyle}
          >
            {description}
          </Text>
          {children}
        </Flex>
        <div className={imagePanelStyle}>
          <img
            className={imageStyle}
            src={imageSrc}
            alt=""
            loading="eager"
            style={{
              ...(imagePosition ? { objectPosition: imagePosition } : {}),
              ...(imageScale
                ? {
                    transform: `scale(${imageScale})`,
                    transformOrigin: imagePosition ?? "center bottom",
                  }
                : {}),
            }}
          />
        </div>
      </Flex>
    </>
  );
};
